import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import { Editor, Toolbar, NgxEditorModule } from "ngx-editor";
import {ToastrService} from "ngx-toastr";
import {forkJoin, Observable} from "rxjs";
import {ConfirmPopupComponent} from "src/app/common/confirm-popup/confirm-popup.component";
import {ToolboxService} from "src/app/common/toolbox/toolbox.service";
import {addPasteSanitizer} from "src/app/common/util/plugins";
import {getProductOwnerStatusColor, getStatusDescriptionFromColor} from "src/app/common/util/product-util";
import {PortfolioStatusService} from "src/app/portfolio-review/portfolio-status/portfolio-status.service";
import {SharedEventsService} from "../../common/shared-events-service";
import {UserDTO, UserService} from "../../common/user.service";
import {
  branches,
  getBackgroundColorOfStatus,
  phases,
  PRODUCT_MILESTONE_CONFIGS,
  PRODUCT_MILESTONE_PHASE_PERIODS,
  statuses,
  squadProfiles
} from "../../common/util/const";
import {DateUtil} from "../../common/util/date-util";
import {FormUtil} from "../../common/util/form-util";
import {applyRequiredMilestoneControls, getRequiredMilestoneControls} from "../../common/util/phase-milestone-util";
import {getValueStreamsByBranch} from "../../common/util/value-stream.util";
import {DataDomainOption, IdAndNameOption, ProductService} from "../../product/product.service";
import {setupMilestoneStatusAutomation, computeMilestoneStatus} from 'src/app/common/util/status-util';
import {ProgramService} from "src/app/program/program.service";
import {ReferentialService} from "src/app/common/referential/referential.service";
import {EnablerService, Enabler} from "../../enabler/enabler.service";
import {ScopeChip, ScopeNode, ScopeTreeUtil} from "../../common/util/scope-tree-util";
import { PopupHeaderComponent } from "../../common/popup-header/popup-header.component";
import { NgSelectComponent, NgOptionTemplateDirective, NgLabelTemplateDirective } from "@ng-select/ng-select";
import { MatTooltip } from "@angular/material/tooltip";
import { UserAutocompleteComponent } from "../../common/user-autocomplete/user-autocomplete.component";
import { NgTemplateOutlet, NgClass } from "@angular/common";
import { MatMenuTrigger, MatMenu } from "@angular/material/menu";
import { MatIcon } from "@angular/material/icon";
import { MatFormField, MatSuffix } from "@angular/material/select";
import { MatInput } from "@angular/material/input";
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker, MatDateRangeInput, MatStartDate, MatEndDate, MatDateRangePicker } from "@angular/material/datepicker";
import { MatCheckbox } from "@angular/material/checkbox";

interface NodeToDisplay {
  node: ScopeNode;
  depth: number;
  leafStats: { selected: number; total: number };
}

@Component({
    selector: "app-add-product",
    templateUrl: "./add-product.component.html",
    styleUrls: ["./add-product.component.scss"],
    imports: [PopupHeaderComponent, FormsModule, ReactiveFormsModule, NgSelectComponent, MatTooltip, NgxEditorModule, UserAutocompleteComponent, NgTemplateOutlet, NgOptionTemplateDirective, MatMenuTrigger, MatIcon, MatMenu, NgClass, MatFormField, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, NgLabelTemplateDirective, MatDateRangeInput, MatStartDate, MatEndDate, MatDateRangePicker, MatCheckbox]
})
export class AddProductComponent implements OnInit, OnDestroy {
  @ViewChild("fileInput") fileInput!: ElementRef;
  @ViewChild("communicationKitInput") communicationKitInput!: ElementRef;
  @ViewChild("demoInput") demoInput!: ElementRef;
  getProductOwnerStatusColor = getProductOwnerStatusColor;
  getStatusDescriptionFromColor = getStatusDescriptionFromColor;
  computedMilestoneStatus = computeMilestoneStatus;
  addProductForm: FormGroup;
  enablers: Enabler[] = [];
  branches = branches;
  selectableBranches: string[] = branches;
  private readonly DATA_PRODUCT_BRANCHES = ["EP", "OT", "GRP", "RC"];
  tags = [
    {label: "New", value: "New", disabled: false},
    {label: "Hold", value: "Hold", disabled: false},
    {label: "Idea", value: "Idea", disabled: false},
    {label: "Poc", value: "Poc", disabled: false},
    {label: "Make", value: "Make", disabled: false},
    {label: "Buy", value: "Buy", disabled: false},
    {label: "Agentic & GenAI", value: "Ai", disabled: false},
  ];
  hostingOptions = ["Cloud", "Hybrid", "On Premise"];
  possibleTags: string[];
  phasesPeriods = PRODUCT_MILESTONE_PHASE_PERIODS;
  dateFields: string[];
  dateError: string | null = null;
  // Date controls that are mandatory because of the current phase change
  requiredMilestoneControls: string[] = [];
  squadProfiles = squadProfiles;
  phases = phases.map((phase) => phase.name);
  statusesNames = statuses.map((status) => status.name);
  maturityLevels = [
    {
      label: "Low",
      description: "Product with few deployments or with deployment times often exceeded.",
    },
    {
      label: "Average",
      description: "Product with either adoption issues or occasional deployment delays, but not every time.",
    },
    {
      label: "High",
      description: "Great product, easy to adopt and easy to deploy.",
    },
  ];

  rationalizationDecisions = [
    {
      label: "Retain",
      description: "The application is retained as-is",
    },
    {
      label: "Merge",
      description: "Combine similar applications when possible",
    },
    {
      label: "Adapt / Reduce",
      description: "Adjustments such as: number of users, licenses, support level, infrastructure",
    },
    {
      label: "Decommission",
      description: "The application is phased out and removed from the portfolio",
    },
  ];

  productTypeOptions = ["Legacy", "Digital", "Inno"];
  entityOwnerOptions: IdAndNameOption[] = [];

  deliveryCenterOptions: IdAndNameOption[] = [];

  specialFieldIsRequired: any = {};
  assets: Array<any> = [];
  products: any[] = [];
  isLocalProduct = false;
  isDataProductMode = false;
  isEnablerProductMode = false;
  userLbu: any = null;
  fieldVisibility: {[key: string]: boolean} = {};

  allProductNames: string[] = [];

  // Map of tab names to the fields they contain
  private readonly TAB_FIELDS = {
    productDetails: [
      "name", "description", "logo", "squadProfile", "associatedProductId", "jiraProjectKey", "finopsBsName", "productType", "tags", "hosting", "showInSearchProduct", "maturity", "rationalizationDecision",
      "businessRequirement", "productRequesters", "programRequesters", "typeOfNeed", "sourceSystem", "associatedDataTable", "Sponsor"
    ],
    organization: [
      "branches", "valueStreams", "domain", "discipline", "entityOwner", "deliveryCenter", "program", "dataPlatform", "dataProductImplementationSquad", "dataDomains", "enabler"
    ],
    contacts: [
      "assets", "pilot", "Sponsor", "Product Owner", "Asset / Domain Lead", "Tech Lead / IT Scaler", "Change Coordinator", "Key User", "Dynamic Lead", "PVM", "UX / UI Designer", "SME", "Data Product Owner", "Data Product PO", "Data Product Value Manager", "Data Product Tech Lead", "Data Product Architect", "Data Preparation Lead", "Data Product Portfolio Manager"
    ],
    milestones: [
      "preFramingStartDate", "preFramingEndDate", "framingStartDate", "framingEndDate", "buildStartDate", "buildEndDate", "growthStartDate", "growthEndDate", "maintainStartDate", "maintainEndDate", "sandboxStartDate", "decommissionedDate", "discardedDate", "minDeploymentTime"
    ]
  };

  private readonly FIELD_REQUIREMENTS = {
    ALWAYS_REQUIRED: ["name", "branches", "phase"],

    DATA_PRODUCT: {
      REQUIRED_FIELDS: [
        "dataPlatform",
        "dataDomains",
        "deliveryCenter",
        "entityOwner"
      ],
    },

    LOCAL_PRODUCT: {
      PHASE_REQUIREMENTS: {
        SANDBOX: [],
        "PRE-FRAMING": [],
        FRAMING: [],
        "BUILD 1ST DEPLOY": [],
        GROWTH: [],
        MAINTAIN: [],
        DISCARDED: [],
      },
      VISIBLE_FIELDS: [
        "name",
        "phase",
        "description",
        "maturity",
        "logo",
        "branches",
        "valueStreams",
        "domain",
        "pilot",
        "sponsors",
        "productOwners",
        "assetLeads",
        "changeCoordinators",
        "preFramingStartDate",
        "preFramingEndDate",
        "framingStartDate",
        "framingEndDate",
        "buildStartDate",
        "buildEndDate",
        "growthStartDate",
        "growthEndDate",
        "maintainStartDate",
        "maintainEndDate",
        "preFramingStartDatePlanned",
        "preFramingEndDatePlanned",
        "framingStartDatePlanned",
        "framingEndDatePlanned",
        "buildStartDatePlanned",
        "buildEndDatePlanned",
        "growthStartDatePlanned",
        "growthEndDatePlanned",
        "maintainStartDatePlanned",
        "maintainEndDatePlanned",
        "preFramingStatus",
        "framingStatus",
        "buildStatus",
        "growthStatus",
        "maintainStatus",
        "decommissionedDate",
        "discardedDate",
        "minDeploymentTime",
      ],
      FORBIDDEN_PHASES: ["SANDBOX", "DISCARDED"],
    },

    REGULAR_PRODUCT: {
      ALWAYS_REQUIRED: [],
      PHASE_REQUIREMENTS: {
        SANDBOX: [],
        "PRE-FRAMING": ["productType"],
        FRAMING: ["productType"],
        "BUILD 1ST DEPLOY": ["productType", "productOwners", "itScaler", "squadProfile"],
        "GROWTH": ["productType", "productOwners", "itScaler", "squadProfile"],
        "MAINTAIN": ["productType", "productOwners", "itScaler", "squadProfile"],
        DISCARDED: [],
      },
    },

    SPECIAL_BRANCHES_NO_PILOT: ["MS", "GRP"],
  };

  CONTACTS_LIST = [
    "Sponsor",
    "Product Owner",
    "Asset / Domain Lead",
    "Tech Lead / IT Scaler",
    "Change Coordinator",
    "Key User",
    "Dynamic Lead",
    "PVM",
    "UX / UI Designer",
    "SME",
    "Data Product Owner",
    "Data Product PO",
    "Data Product Value Manager",
    "Data Product Tech Lead",
    "Data Product Architect",
    "Data Preparation Lead",
    "Data Product Portfolio Manager",
  ];

  selectedContacts: Map<string, UserDTO[]> = new Map<string, UserDTO[]>();

  // Upload files for local products
  uploadedCommunicationKits: File[] = [];
  uploadedDemos: File[] = [];

  // Existing documents when editing a product
  existingCommunicationKits: any[] = [];
  existingDemos: any[] = [];

  // Backup of original documents (for cancel functionality)
  originalExistingCommunicationKits: any[] = [];
  originalExistingDemos: any[] = [];

  // Documents marked for deletion (only deleted on save)
  documentsToDelete: any[] = [];

  public editor: Editor;
  public toolbar: Toolbar = [
    ["bold", "italic"],
    ["underline", "strike"],
    ["ordered_list", "bullet_list"],
    [{heading: ["h1", "h2", "h3", "h4", "h5", "h6"]}],
    ["text_color"],
    ["align_left", "align_center", "align_right", "align_justify"],
  ];

  valueStreamFields: any = [];
  oldSelectedBranches: Array<string> = [];
  description: string = "";

  domains: Array<string>;
  disciplines: any = {};
  process: any = {};
  isAdmin: boolean = false;

  programOptions: Array<string>;
  dataDomainOptions: DataDomainOption[] = [];
  typeOfNeedOptions: IdAndNameOption[] = [];
  sourceSystemOptions: string[] = [];
  dataPlatformOptions: string[] = [];
  dataProductImplementationSquadOptions: IdAndNameOption[] = [];
  showOtherSourceSystem: boolean = false;

  private readonly OTHER_SOURCE_SYSTEM_OPTION = "Other";

  selectedDepScopeBranch: string = null;
  depScopeSearchQuery: string = '';
  scopeByBranch: { [branch: string]: ScopeNode[] } = {};
  nodesToDisplay: NodeToDisplay[] = [];
  selectedChips: ScopeChip[] = [];
  assetsByBranchLoading: boolean = false;

  newSiteNames: { [assetId: number]: string } = {};
  temporarySiteIdCounter: number = -1;

  intervalCheckRange: any;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly toastrService: ToastrService,
    private readonly productService: ProductService,
    private readonly toolboxService: ToolboxService,
    private readonly programService: ProgramService,
    private readonly portfolioStatusService: PortfolioStatusService,
    public dialogRef: MatDialogRef<AddProductComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly sharedEventsService: SharedEventsService,
    private readonly userService: UserService,
    private readonly enablerService: EnablerService,
    private readonly referentialService: ReferentialService
  ) {
    this.isLocalProduct = this.data?.isLocalProduct ?? false;
    this.isDataProductMode = this.data?.dataProduct || this.data?.product?.dataProduct;
    this.selectableBranches = this.isDataProductMode
      ? this.branches.filter((branch) => this.DATA_PRODUCT_BRANCHES.includes(branch))
      : this.branches;
    this.isEnablerProductMode = this.data?.enablerProduct || this.data?.product?.enablerProduct;
    if (this.isLocalProduct && this.data?.userLbu) {
      this.userLbu = this.data.userLbu;
    }

    this.addProductForm = this.fb.group({
      logo: [null],
      name: ["", [Validators.required]],
      branches: [null, Validators.required],
      assets: [[]],
      phase: ["", Validators.required],
      pilot: [null],
      tags: [[]],
      hosting: [null],
      squadProfile: [""],

      preFramingStartDate: null,
      preFramingEndDate: null,
      framingStartDate: null,
      framingEndDate: null,
      buildStartDate: null,
      buildEndDate: null,
      growthStartDate: null,
      growthEndDate: null,
      maintainStartDate: null,
      maintainEndDate: null,
      preFramingStartDatePlanned: null,
      preFramingEndDatePlanned: null,
      framingStartDatePlanned: null,
      framingEndDatePlanned: null,
      buildStartDatePlanned: null,
      buildEndDatePlanned: null,
      growthStartDatePlanned: null,
      growthEndDatePlanned: null,
      maintainStartDatePlanned: null,
      maintainEndDatePlanned: null,
      preFramingStatus: [null],
      framingStatus: [null],
      buildStatus: [null],
      growthStatus: [null],
      maintainStatus: [null],

      decommissionedDate: null,
      discardedDate: null,
      jiraProjectKey: [""],
      finopsBsName: [""],
      description: [""],
      associatedProductId: [null],
      showInSearchProduct: [true],
      productOwnerStatus: [null],
      maturity: [null],
      minDeploymentTime: [null],
      domain: [null],
      productType: ["Digital"],
      discipline: [null],
      process: [null],
      rationalizationDecision: [null],
      entityOwner: [null],
      deliveryCenter: [null],
      program: [""],
      enabler: [this.data?.enablerId || null, this.isEnablerProductMode ? Validators.required : []],
      runCostApp: [null],
      runCostInfra: [null],
      globalItRunCost: [null],
      buildCostProject: [null],
      buildCostEvo: [null],
      dataProductPreframingCost: [null],
      dataProductFramingCost: [null],
      dataProductBuildCost: [null],
      dataProductRunMaintainCost: [null],
      ddo: [[]],
      dsl: [[]],
      isEnablerProduct: [false],
      /* Data Product Fields */
      isDataProduct: [this.isDataProductMode],
      businessRequirement: [""],
      productRequesters: [[]],
      programRequesters: [[]],
      typeOfNeed: [null],
      sourceSystem: [null],
      sourceSystemOther: [""],
      associatedDataTable: [""],
      dataPlatform: [null],
      dataProductImplementationSquad: [null],
      dataDomains: [[]],
      sandboxStartDate: [null],
      publishedInDataMarket: [false],
    });

    /** Fixing weird bug where the date range is considered invalid when the start date is modified before the end date **/
    this.intervalCheckRange = setInterval(() => {
      this.phasesPeriods.forEach((period) => {
        this.addProductForm.controls[period.start].updateValueAndValidity();
        this.addProductForm.controls[period.end].updateValueAndValidity();
      });
    }, 1000);

    for (const contact of this.CONTACTS_LIST) {
      this.addProductForm.addControl(contact, new FormControl(contact == "Tech Lead / IT Scaler" ? "" : []));
    }

    this.initializeValidators();
    this.refreshFieldVisibility();

    this.possibleTags = this.tags.map((tag) => tag.value.toLowerCase());
    this.dateFields = this.phasesPeriods.flatMap((period) => [period.start, period.end]);

    this.phases = this.isLocalProduct
      ? this.phases.filter((phase) => !this.FIELD_REQUIREMENTS.LOCAL_PRODUCT.FORBIDDEN_PHASES.includes(phase))
      : this.phases;

    if (this.data?.product?.contacts) {
      for (let contact of this.data.product.contacts) {
        this.selectedContacts[contact.role] = this.selectedContacts[contact.role] ?? [];
        this.selectedContacts[contact.role].push({email: contact.email, fullName: contact.fullName});
      }
    }
  }

  ngOnInit(): void {
    this.editor = new Editor();
    this.isAdmin = this.userService.getUserIsAdmin() ?? false;
    addPasteSanitizer(this.editor);

    // Fetch the list of products for the associated product dropdown
    this.productService.getAllProducts().subscribe((products) => {
      this.products = products;
    });

    this.productService.getAllDomains().subscribe((domains) => {
      this.domains = domains;
    });

    this.referentialService.getReferences("entity-owners").subscribe((entityOwners) => {
      this.entityOwnerOptions = entityOwners;
    });
    this.referentialService.getReferences("delivery-centers").subscribe((deliveryCenters) => {
      this.deliveryCenterOptions = deliveryCenters;
    });
    this.productService.getAllTypeOfNeeds().subscribe((typeOfNeeds) => {
      this.typeOfNeedOptions = typeOfNeeds;
    });

    this.productService.getAllSourceSystems().subscribe((sourceSystems) => {
      this.sourceSystemOptions = [...sourceSystems, this.OTHER_SOURCE_SYSTEM_OPTION];
      this.initializeSourceSystemSelection(this.data?.product?.sourceSystem ?? null);
    });

    this.productService.getAllDataPlatforms().subscribe((dataPlatforms) => {
      this.dataPlatformOptions = dataPlatforms;
    });

    this.productService.getAllDataProductImplementationSquads().subscribe((dataProductImplementationSquads) => {
      this.dataProductImplementationSquadOptions = dataProductImplementationSquads;
    });

    this.programService.getAllPrograms().subscribe((programs) => {
      this.programOptions = programs.map(p => p.name);
    });

    this.enablerService.getEnablersGroupedByFamily().subscribe((groupedEnablers) => {
      // Flatten the grouped enablers to get unique enabler families
      const enablersMap = new Map<number, Enabler>();
      Object.values(groupedEnablers).forEach(enablerArray => {
        enablerArray.forEach(enabler => {
          enablersMap.set(enabler.id, enabler);
        });
      });
      this.enablers = Array.from(enablersMap.values());
    });

    this.productService.getAllDisciplines().subscribe((disciplines) => {
      this.disciplines = disciplines;
      this.refreshFieldVisibility();
    });

    this.productService.getAllProcess().subscribe((process) => {
      for (let discipline in process) {
        process[discipline] = process[discipline].map(p => p.name);
      }
      this.process = process;
      this.refreshFieldVisibility();
    });

    this.productService.getAllProductsNames().subscribe(names => {
      this.allProductNames = names.map(n => n.trim().toLowerCase());
      const nameControl = this.addProductForm.get('name');
      if (nameControl) {
        nameControl.setValidators([
          Validators.required,
          (control: FormControl) => {
            const name = (control.value ?? '').trim().toLowerCase();
            if (!name) return null;
            // Only block for duplicate name if adding (no id), or if editing and changing to a name that exists for another product
            if (!this.data?.product?.id && this.allProductNames.includes(name)) {
              return {productNameExists: true};
            }
            return null;
          }
        ]);
        nameControl.updateValueAndValidity();
      }
    });

    if (this.data?.product?.id) {
      this.description = this.data.product.description ?? "";

      const dateValues = this.dateFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: this.data.product[field] ? DateUtil.dateWithoutTimezone(this.data.product[field]) : null,
        }),
        {}
      );

      this.addProductForm.patchValue({
        logo: this.data.product.logo,
        name: this.data.product.name,
        branches: this.data.product.branches,
        assets: this.data.product.assets,
        pilot: this.data.product.pilot,
        phase: this.data.product.phase,
        ...dateValues,
        decommissionedDate: this.data.product.decommissionedDate
          ? DateUtil.dateWithoutTimezone(this.data.product.decommissionedDate)
          : "",
        discardedDate: this.data.product.discardedDate
          ? DateUtil.dateWithoutTimezone(this.data.product.discardedDate)
          : "",
        description: this.description,
        tags: this.possibleTags.filter((tag) => this.data.product.tags?.includes(tag)).map((tag) => tag.charAt(0).toUpperCase() + tag.slice(1)),
        squadProfile: this.data.product.squadProfile,
        hosting: this.data.product.hosting ?? null,
        jiraProjectKey: this.data.product.jiraProjectKey,
        finopsBsName: this.data.product.finopsBsName,
        associatedProductId: this.data.product.associatedProductId ?? null, // Set the associated product if it exists
        showInSearchProduct: this.data.product.showInSearchProduct,
        productOwnerStatus: this.data.product.productOwnerStatus ?? null,
        maturity: this.data.product.maturity ?? null,
        minDeploymentTime: this.data.product.minDeploymentTime ?? null,
        domain: this.data.product.domain,
        productType: this.data.product.productType ?? "Digital",
        discipline: this.data.product.discipline ?? null,
        process: this.data.product.process ?? null,
        rationalizationDecision: this.data.product.rationalizationDecision ?? null,
        entityOwner: this.data.product.entityOwnerId ?? null,
        deliveryCenter: this.data.product.deliveryCenterId ?? null,
        program: this.data.product.program ?? null,
        enabler: this.data.product.enabler ?? null,
        runCostApp: this.data.product.runCostApp ?? null,
        runCostInfra: this.data.product.runCostInfra ?? null,
        globalItRunCost: this.data.product.globalItRunCost ?? null,
        buildCostProject: this.data.product.buildCostProject ?? null,
        buildCostEvo: this.data.product.buildCostEvo ?? null,
        dataProductPreframingCost: this.data.product.dataProductPreframingCost ?? null,
        dataProductFramingCost: this.data.product.dataProductFramingCost ?? null,
        dataProductBuildCost: this.data.product.dataProductBuildCost ?? null,
        dataProductRunMaintainCost: this.data.product.dataProductRunMaintainCost ?? null,
        preFramingStatus: this.data.product.preFramingStatus ?? null,
        framingStatus: this.data.product.framingStatus ?? null,
        buildStatus: this.data.product.buildStatus ?? null,
        growthStatus: this.data.product.growthStatus ?? null,
        maintainStatus: this.data.product.maintainStatus ?? null,
        ddo: this.data.product.ddo ?? [],
        dsl: this.data.product.dsl ?? [],
        enablerProduct: this.data.product.enablerProduct ?? false,
        dataProduct: this.data.product.dataProduct ?? false,
        businessRequirement: this.data.product.businessRequirement ?? "",
        productRequesters: (this.data.product.productRequesters ?? []).map((product) => product?.id ?? product),
        programRequesters: (this.data.product.programRequesters ?? []).map((program) => program?.name ?? program),
        typeOfNeed: this.data.product.typeOfNeedId ?? null,
        sourceSystem: null,
        sourceSystemOther: "",
        associatedDataTable: this.data.product.associatedDataTable ?? "",
        dataPlatform: this.data.product.dataPlatform ?? null,
        dataProductImplementationSquad: this.data.product.dataProductImplementationSquadId ?? null,
        dataDomains: this.data.product.dataDomainIds ?? [],
        sandboxStartDate: this.data.product.sandboxStartDate
          ? DateUtil.dateWithoutTimezone(this.data.product.sandboxStartDate)
          : null,
        publishedInDataMarket: this.data.product.publishedInDataMarket ?? false,
      });

      for (let contactKey in this.selectedContacts) {
        this.addProductForm.get(contactKey).setValue(this.selectedContacts[contactKey] ?? []);
      }

      this.oldSelectedBranches = this.data.product.branches;

      if (this.data.product.branches != null) {
        if (!this.isDataProductMode) {
          this.createValueStreamDropdown();
        }
        if (this.isDataProductMode) {
          this.loadDataDomains(this.data.product.branches);
        }
      }

      this.getAssetsByBranches(this.addProductForm.get("branches")?.value ?? [], false);
      this.fieldVisibility.assets = !this.isDataProductMode && this.addProductForm.get("branches")?.value?.includes("RC");

      // Load existing documents for local products
      if (this.isLocalProduct && this.data.product.id) {
        this.loadExistingDocuments(this.data.product.id);
      }
    }

    this.addProductForm.get("branches")?.valueChanges.subscribe((selectedBranches) => {
      // Clean up scope data for branches that were removed
      const activeBranches = new Set(selectedBranches ?? []);
      Object.keys(this.scopeByBranch).forEach(b => {
        if (!activeBranches.has(b)) delete this.scopeByBranch[b];
      });

      if (this.selectedDepScopeBranch && !activeBranches.has(this.selectedDepScopeBranch)) {
        this.selectedDepScopeBranch = null;
      }

      this.getAssetsByBranches(selectedBranches, true);
      this.fieldVisibility.assets = !this.isDataProductMode && selectedBranches.includes("RC");

      if (!this.isDataProductMode) {
        this.addOrRemoveValueStreamDropdownBasedOnBranches(selectedBranches);
      }
      if (this.isDataProductMode) {
        this.loadDataDomains(selectedBranches);
      }
      this.setRequirementsBasedOnChosenPhase();
    });

    this.addProductForm.get("squadProfile").valueChanges.subscribe((value) => {
      if (value !== "Multi Product") {
        this.addProductForm.get("associatedProductId").setValue(null);
        this.fieldVisibility.associatedProduct = false;
      } else {
        this.fieldVisibility.associatedProduct = true;
      }    
    });

    this.addProductForm.get("productType").valueChanges.subscribe((value) => {      this.valueStreamFields.forEach((field) => {
        this.setFieldRequired(field.formControlName, this.addProductForm.get('productType')?.value != 'Legacy' && this.addProductForm.get('productType')?.value != 'Inno');
      });

      this.setRequirementsBasedOnChosenPhase(["productType"]);
    });

    this.addProductForm.get("domain")?.valueChanges.subscribe(() => {
      this.addProductForm.get('discipline')?.setValue(null);
      this.addProductForm.get('process')?.setValue(null);
      this.refreshFieldVisibility();
    });

    this.addProductForm.get("discipline")?.valueChanges.subscribe(() => {
      this.addProductForm.get('process')?.setValue(null);
      this.refreshFieldVisibility();
    });

    // Use shared milestone status automation for all product phases
    setupMilestoneStatusAutomation(this.addProductForm, this.data.product ?? {}, PRODUCT_MILESTONE_CONFIGS);

    this.onTagChange(this.addProductForm.get("tags").value ?? []);
    const phaseControl = this.addProductForm.get("phase");
    const pilotControl = this.addProductForm.get("pilot");

    if (!pilotControl?.value) {
      pilotControl.enable({emitEvent: false});
    }
    if ((phaseControl?.value === "BUILD 1ST DEPLOY" || phaseControl?.value === "GROWTH" || phaseControl?.value === "MAINTAIN") && pilotControl?.value) {
      pilotControl.disable({emitEvent: false});
    }

    // Update requirements based on the initial phase value.
    this.setRequirementsBasedOnChosenPhase();
    this.updateMilestoneRequirements();

    // Subscribe to phase changes to update validation and flag.
    phaseControl?.valueChanges.subscribe(() => {
      this.setRequirementsBasedOnChosenPhase();
      this.updateMilestoneRequirements();
      this.requiredMilestoneControls.forEach((name) => this.addProductForm.get(name)?.markAsTouched());
    });

    this.addProductForm.get("sourceSystem")?.valueChanges.subscribe((value) => {
      this.showOtherSourceSystem = value === this.OTHER_SOURCE_SYSTEM_OPTION;
      if (!this.showOtherSourceSystem) {
        this.addProductForm.get("sourceSystemOther")?.setValue("", {emitEvent: false});
      }
    });
  }

  ngOnDestroy() {
    this.editor?.destroy();
    clearInterval(this.intervalCheckRange);
  }

  onDateChange(periodIndex: number): void {
    this.resetMilestonesDatesIfOutOfRange(periodIndex);
  }

  resetMilestonesDatesIfOutOfRange(periodIndex: number): void {
    const period = this.phasesPeriods[periodIndex];

    FormUtil.resetMilestonesDatesIfOutOfRange(
      this.addProductForm,
      period.start,
      period.end,
      [period.start, period.end]
    );
  }

  changeProductOwnerStatus(status: string): void {
    this.addProductForm.patchValue({productOwnerStatus: status});
  }

  isSandboxOrPreOrFramingPhase(): boolean {
    const phase = this.addProductForm.get("phase")?.value;
    return phase === "SANDBOX" || phase === "PRE-FRAMING" || phase === "FRAMING";
  }

  initializeValidators() {
    this.FIELD_REQUIREMENTS.ALWAYS_REQUIRED.forEach((field) => {
      this.setFieldRequired(field, true);
    });

    if (this.isDataProductMode) {
      this.FIELD_REQUIREMENTS.DATA_PRODUCT.REQUIRED_FIELDS.forEach((field) => {
        this.setFieldRequired(field, true);
      });
    }

    if (!this.isLocalProduct) {
      this.FIELD_REQUIREMENTS.REGULAR_PRODUCT.ALWAYS_REQUIRED.forEach((field) => {
        this.setFieldRequired(field, true);
      });
    }
  }

  private setFieldRequired(fieldName: string, required: boolean) {
    const control = this.addProductForm.get(fieldName);
    if (control) {
      if (required) {
        control.setValidators(Validators.required);
        this.specialFieldIsRequired[fieldName] = true;
      } else {
        control.clearValidators();
        this.specialFieldIsRequired[fieldName] = false;
      }
      control.updateValueAndValidity();
    }
  }

  private isPilotRequired(): boolean {
    return this.assets && this.assets.length > 0;
  }

  private refreshFieldVisibility(): void {
    const hasDomain = !!this.addProductForm.get("domain")?.value;
    const hasDiscipline = !!this.addProductForm.get("discipline")?.value;

    this.fieldVisibility = {
      dataProductDetails: this.isDataProductMode,
      description: true,
      squadProfile: !this.isLocalProduct && !this.isDataProductMode,
      associatedProduct: !this.isLocalProduct && !this.isDataProductMode && this.addProductForm.get("squadProfile")?.value === "Multi Product",
      jiraProjectKey: !this.isLocalProduct && !this.isDataProductMode,
      finopsBsName: !this.isLocalProduct && !this.isDataProductMode,
      productType: !this.isLocalProduct && !this.isDataProductMode,
      tags: !this.isLocalProduct && !this.isDataProductMode,
      technologies: !this.isLocalProduct,
      hosting: !this.isLocalProduct && !this.isDataProductMode,
      maturity: !this.isDataProductMode,
      rationalizationDecision: !this.isLocalProduct && !this.isDataProductMode,
      uploadDocuments: this.isLocalProduct,
      branches: true,
      valueStreams: !this.isDataProductMode,
      assets: !this.isDataProductMode && !this.isLocalProduct && this.addProductForm.get("branches")?.value?.includes("RC"),
      pilot: !this.isDataProductMode,
      sponsors: !this.isDataProductMode,
      sponsorRequester: this.isDataProductMode,
      dataProductPo: this.isDataProductMode,
      productOwners: !this.isDataProductMode,
      assetDomainLeads: !this.isDataProductMode,
      itScaler: !this.isDataProductMode && !this.isLocalProduct,
      changeCoordinators: !this.isDataProductMode,
      keyUsers: !this.isDataProductMode && !this.isLocalProduct,
      dynamicLead: !this.isDataProductMode,
      pvm: !this.isDataProductMode,
      uxUiDesigners: !this.isDataProductMode,
      ddo: !this.isDataProductMode,
      dsl: !this.isDataProductMode,
      sme: !this.isDataProductMode,
      dataProductValueManager: this.isDataProductMode,
      dataProductOwner: this.isDataProductMode,
      dataProductTechlead: this.isDataProductMode,
      dataProductArchitect: this.isDataProductMode,
      dataPreparationLead: this.isDataProductMode,
      dataProductPortfolioManager: this.isDataProductMode,
      domain: true,
      discipline: hasDomain && (this.disciplines[this.addProductForm.get("domain")?.value]?.length > 0),
      process: !this.isDataProductMode && hasDiscipline && (this.process[this.addProductForm.get("discipline")?.value]?.length > 0),
      entityOwner: !this.isLocalProduct,
      deliveryCenter: !this.isLocalProduct,
      program: !this.isLocalProduct && !this.isDataProductMode,
      dataPlatform: this.isDataProductMode,
      dataProductImplementationSquad: this.isDataProductMode,
      dataDomains: this.isDataProductMode,
      enabler: this.isEnablerProductMode || this.isDataProductMode,
    };
  }

  private initializeSourceSystemSelection(sourceSystem: string | null): void {
    if (!this.sourceSystemOptions.length) {
      return;
    }

    const hasOfficialSourceSystem = !!sourceSystem && this.sourceSystemOptions.includes(sourceSystem);
    this.showOtherSourceSystem = !!sourceSystem && !hasOfficialSourceSystem;

    let selectedSourceSystem = sourceSystem;
    if (!hasOfficialSourceSystem) {
      if (sourceSystem) {
        selectedSourceSystem = this.OTHER_SOURCE_SYSTEM_OPTION;
      } else {
        selectedSourceSystem = null;
      }
    }

    let sourceSystemOtherValue = "";
    if (!hasOfficialSourceSystem) {
      sourceSystemOtherValue = sourceSystem ?? "";
    }

    this.addProductForm.patchValue({
      sourceSystem: selectedSourceSystem,
      sourceSystemOther: sourceSystemOtherValue
    }, {emitEvent: false});
  }

  private getSourceSystemValue(formValues: any): string | null {
    if (formValues.sourceSystem === this.OTHER_SOURCE_SYSTEM_OPTION) {
      return formValues.sourceSystemOther?.trim() || null;
    }

    return formValues.sourceSystem;
  }

  setRequirementsBasedOnChosenPhase(except?: string[]) {
    const phase = this.addProductForm.get("phase")?.value;

    let phaseRequirements = [];
    if (this.isDataProductMode) phaseRequirements = [];
    else if (this.isLocalProduct) phaseRequirements = this.FIELD_REQUIREMENTS.LOCAL_PRODUCT.PHASE_REQUIREMENTS[phase] ?? [];
    else if (this.addProductForm.get('productType')?.value != 'Legacy' && this.addProductForm.get('productType')?.value != 'Inno') {
      phaseRequirements = this.FIELD_REQUIREMENTS.REGULAR_PRODUCT.PHASE_REQUIREMENTS[phase] ?? [];
    }

    if (except) phaseRequirements = phaseRequirements.filter(f => !except.includes(f));

    phaseRequirements.forEach((field) => {
      let shouldBeRequired = true;

      if (field === "pilot") {
        shouldBeRequired = this.isPilotRequired();
      }

      this.setFieldRequired(field, shouldBeRequired);
    });

    this.clearUnusedFieldValidations(phase, except);
  }

  private updateMilestoneRequirements(): void {
    let requiredControls: string[] = [];
    if (!this.isLocalProduct) {
      requiredControls = getRequiredMilestoneControls(this.phasesPeriods, this.addProductForm.get("phase")?.value, !this.data?.product?.id);
      if (!this.isDataProductMode) {
        // The sandbox start date is only available for data products
        requiredControls = requiredControls.filter((name) => name !== "sandboxStartDate");
      }
    }
    applyRequiredMilestoneControls(this.addProductForm, this.requiredMilestoneControls, requiredControls);
    this.requiredMilestoneControls = requiredControls;
  }

  private clearUnusedFieldValidations(currentPhase: string, except: string[]) {
    let allPossiblePhaseFields = [
      ...Object.values(this.FIELD_REQUIREMENTS.LOCAL_PRODUCT.PHASE_REQUIREMENTS).flat(),
      ...Object.values(this.FIELD_REQUIREMENTS.REGULAR_PRODUCT.PHASE_REQUIREMENTS).flat(),
    ];

    if (except) allPossiblePhaseFields = allPossiblePhaseFields.filter(f => !except.includes(f));

    let currentPhaseRequirements: string[] = [];
    if (this.isDataProductMode) currentPhaseRequirements = [];
    else if (this.isLocalProduct) currentPhaseRequirements = this.FIELD_REQUIREMENTS.LOCAL_PRODUCT.PHASE_REQUIREMENTS[currentPhase] ?? [];
    else if (this.addProductForm.get('productType')?.value != 'Legacy' && this.addProductForm.get('productType')?.value != 'Inno') {
      currentPhaseRequirements = this.FIELD_REQUIREMENTS.REGULAR_PRODUCT.PHASE_REQUIREMENTS[currentPhase] ?? [];
    }

    allPossiblePhaseFields.forEach((field: string) => {
      if (!currentPhaseRequirements.includes(field)) {
        this.setFieldRequired(field, false);
      }
    });
  }

  getAssetsByBranches(selectedBranches, isAChange) {
    this.assetsByBranchLoading = true;
    this.productService.getAllAssetsByBranches(selectedBranches).subscribe((assets) => {
      this.assetsByBranchLoading = false;
      this.assets = assets;

      if (!this.selectedDepScopeBranch && selectedBranches?.length > 0) {
        this.selectedDepScopeBranch = [...selectedBranches].sort((a, b) => a.localeCompare(b))[0];
      }

      this.buildScopeTreesFromAssets(selectedBranches);

      if (!isAChange && this.data.product.scopes) {
        this.selectScopeBasedOnExisting();
      }

      this.buildNodesToDisplay();

      if (
        isAChange &&
        this.addProductForm.get("pilot")?.value?.name &&
        !this.assets.map((s) => s.name).includes(this.addProductForm.get("pilot").value.name)
      ) {
        this.addProductForm.patchValue({pilot: null});
        this.addProductForm.get("pilot").enable();
        this.addProductForm.updateValueAndValidity();
      }

      this.setRequirementsBasedOnChosenPhase();
    });
  }

  onTagCheckboxChange(tagValue: string, event: any): void {
    const currentTags = this.addProductForm.get("tags")?.value ?? [];
    let updatedTags: string[];

    if (event.target.checked) {
      updatedTags = [...currentTags, tagValue];
    } else {
      updatedTags = currentTags.filter((tag: string) => tag !== tagValue);
    }

    this.addProductForm.get("tags")?.setValue(updatedTags);
    this.onTagChange(updatedTags);
  }

  onTagRadioChange(group: string, tagValue: string, event: any): void {
    const currentTags = this.addProductForm.get("tags")?.value ?? [];
    let updatedTags: string[] = [...currentTags];

    let isTagPresent = updatedTags.includes(tagValue);

    if (group === "ideaPoc") {
      // Remove both Idea and Poc, then add the selected one if checked
      updatedTags = updatedTags.filter((tag) => tag !== "Idea" && tag !== "Poc");
      if (event.target.checked && !isTagPresent) {
        updatedTags.push(tagValue);
      }
    } else if (group === "makeBuy") {
      // Remove both Make and Buy, then add the selected one if checked
      updatedTags = updatedTags.filter((tag) => tag !== "Make" && tag !== "Buy");
      if (event.target.checked && !isTagPresent) {
        updatedTags.push(tagValue);
      }
    }

    this.addProductForm.get("tags")?.setValue(updatedTags);
  }

  onTagChange(selected: (string | { value: string })[]): void {
    this.tags.forEach((tag) => (tag.disabled = false));
    const selectedValues = selected.map((s) => (typeof s === "string" ? s.toLowerCase() : s.value.toLowerCase()));

    if (selectedValues.includes("idea")) {
      this.tags.find((t) => t.value.toLowerCase() === "poc").disabled = true;
    }
    if (selectedValues.includes("poc")) {
      this.tags.find((t) => t.value.toLowerCase() === "idea").disabled = true;
    }
    if (selectedValues.includes("buy")) {
      this.tags.find((t) => t.value.toLowerCase() === "make").disabled = true;
    }
    if (selectedValues.includes("make")) {
      this.tags.find((t) => t.value.toLowerCase() === "buy").disabled = true;
    }

    this.tags = [...this.tags];
  }

  onSubmit(): void {
    if (this.addProductForm.valid) {
      // Remove unselected new sites before saving
      this.removeUnselectedNewSites();

      const formValues = this.addProductForm.getRawValue();
      formValues.description = this.description;
      const productToSave = {
        id: this.data?.product?.id,
        logo: formValues.logo,
        name: formValues.name,
        branches: formValues.branches ?? [],

        phase: formValues.phase,
        pilot: formValues.pilot,
        tags: (formValues.tags || []).map((tag) => (typeof tag === "string" ? tag.toLowerCase() : tag.value?.toLowerCase?.() ?? tag)),
        hosting: this.fieldVisibility.hosting ? formValues.hosting : null,
        description: formValues.description ?? "",

        valueStreams: Object.keys(formValues)
          .filter((key) => key.startsWith("value-stream-") && formValues[key])
          .map((key: string) => ({
            branch: key.split("-")[2],
            name: formValues[key],
          })),
        contacts:
          this.CONTACTS_LIST.flatMap(contactRole => formValues[contactRole] == "" ? [] : formValues[contactRole].map((user: UserDTO) => ({
            email: user.email,
            fullName: user.fullName,
            role: contactRole
          }))),
        assets:
          formValues.assets?.map((asset) => ({
            id: asset.id,
          })) ?? [],
        squadProfile: formValues.squadProfile,
        statusCreationDate: formValues.statusCreationDate,
        statusTransferDate: formValues.statusTransferDate,
        statusRunDate: formValues.statusRunDate,
        decommissionedDate: formValues.decommissionedDate,
        discardedDate: formValues.discardedDate,
        jiraProjectKey: formValues.jiraProjectKey,
        finopsBsName: formValues.finopsBsName,
        associatedProductId: formValues.associatedProductId,
        showInSearchProduct: formValues.showInSearchProduct,
        productOwnerStatus: formValues.productOwnerStatus ?? null,
        maturity: formValues.maturity ? formValues.maturity.label || formValues.maturity : null,
        minDeploymentTime: formValues.minDeploymentTime,
        domain: formValues.domain,
        productType: formValues.productType,
        discipline: this.disciplines[formValues.domain]?.includes(formValues.discipline) ? formValues.discipline : null,
        process: this.process[formValues.discipline]?.includes(formValues.process) ? formValues.process : null,
        localProduct: this.isLocalProduct,
        rationalizationDecision: formValues.rationalizationDecision?.label ?? formValues.rationalizationDecision,
        entityOwnerId: formValues.entityOwner,
        deliveryCenterId: formValues.deliveryCenter,
        program: formValues.program,
        enabler: formValues.enabler,
        runCostApp: formValues.runCostApp ?? null,
        runCostInfra: formValues.runCostInfra ?? null,
        globalItRunCost: formValues.globalItRunCost ?? null,
        buildCostProject: formValues.buildCostProject ?? null,
        buildCostEvo: formValues.buildCostEvo ?? null,
        dataProductPreframingCost: formValues.dataProductPreframingCost ?? null,
        dataProductFramingCost: formValues.dataProductFramingCost ?? null,
        dataProductBuildCost: formValues.dataProductBuildCost ?? null,
        dataProductRunMaintainCost: formValues.dataProductRunMaintainCost ?? null,
        preFramingStatus: formValues.preFramingStatus ?? null,
        framingStatus: formValues.framingStatus ?? null,
        buildStatus: formValues.buildStatus ?? null,
        growthStatus: formValues.growthStatus ?? null,
        maintainStatus: formValues.maintainStatus ?? null,
        ddo: formValues.ddo ?? [],
        dsl: formValues.dsl ?? [],
        scopes: this.buildScopeToSendOnSave(),
        newSites: this.buildNewSitesToSave(),
        enablerProduct: this.isEnablerProductMode ?? false,
        dataProduct: this.isDataProductMode ?? false,
        businessRequirement: formValues.businessRequirement,
        productRequesters: (formValues.productRequesters ?? []).map((product) => product?.id ?? product).filter((value) => value != null),
        programRequesters: (formValues.programRequesters ?? []).map((program) => program?.name ?? program).filter((value) => !!value),
        typeOfNeedId: formValues.typeOfNeed,
        sourceSystem: this.getSourceSystemValue(formValues),
        associatedDataTable: formValues.associatedDataTable,
        dataPlatform: formValues.dataPlatform,
        dataProductImplementationSquadId: formValues.dataProductImplementationSquad,
        dataDomainIds: formValues.dataDomains ?? [],
        sandboxStartDate: formValues.sandboxStartDate ?? null,
        publishedInDataMarket: formValues.publishedInDataMarket ?? false,
      };

      this.dateFields.forEach((field) => {
        productToSave[field] = formValues[field] ?? null;
      });

      this.productService.saveProduct(productToSave, this.data.portfolioId).subscribe({
        next: (savedProduct) => {
          const productId = savedProduct?.id ?? this.data?.product?.id;

          if (this.isLocalProduct && productId) {
            this.handleDocumentOperations(productId, productToSave);
          } else {
            this.showSuccessAndClose(productToSave);
          }
        },
        error: () => {
          this.toastrService.error("Failed to " + (this.data?.product?.id ? "update" : "add") + " product. Try again.", "Error");
        },
      });
    } else {
      this.toastrService.error("Please fill out the form correctly.", "Error");
    }
  }

  onCancel(): void {
    // Remove all new sites that weren't saved
    for (const branch in this.scopeByBranch) {
      this.removeAllNewSitesFromNodes(this.scopeByBranch[branch]);
    }

    // Restore original state of documents in case user made changes but cancelled
    if (this.isLocalProduct && this.data?.product?.id) {
      this.existingCommunicationKits = [...this.originalExistingCommunicationKits];
      this.existingDemos = [...this.originalExistingDemos];
      this.documentsToDelete = []; // Clear deletion queue
    }
    this.dialogRef.close(null);
  }

  private removeAllNewSitesFromNodes(nodes: ScopeNode[]): void {
    for (const node of nodes) {
      if (node.children?.length) {
        // Remove all new sites
        node.children = node.children.filter(child => !child.isNew);
        this.removeAllNewSitesFromNodes(node.children);
      }
    }
  }

  onUsersSelected(selectedUsers: UserDTO[], key: string): void {
    if (["Sponsor", "Tech Lead / IT Scaler", "Data Product PO", "Data Product Tech Lead", "Data Product Architect", "Data Preparation Lead"].includes(key)) {
      const selectedUser = selectedUsers.length > 0 ? selectedUsers[0] : null;
      this.addProductForm.patchValue({
        [key]: selectedUser ? [selectedUser] : [],
      });
    } else {
      this.addProductForm.patchValue({
        [key]: selectedUsers.map((user) => ({
          ...user,
        })),
      });
    }
  }

  private loadDataDomains(branches: string[]): void {
    if (!branches || branches.length === 0) {
      this.dataDomainOptions = [];
      this.addProductForm.get("dataDomains")?.setValue([]);
      return;
    }

    this.productService.getAllDataDomains(branches).subscribe((dataDomains) => {
      this.dataDomainOptions = dataDomains;
      const currentDomains = this.addProductForm.get("dataDomains")?.value ?? [];
      const availableDomainIds = new Set(dataDomains.map((domain) => domain.id));
      this.addProductForm.get("dataDomains")?.setValue(currentDomains.filter((domainId: number) => availableDomainIds.has(domainId)));
    });
  }

  triggerFileUpload(): void {
    this.fileInput.nativeElement.click();
  }

  handleKeyPress(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.triggerFileUpload();
    }
  }

  onLogoChange(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        this.addProductForm.patchValue({
          logo: reader.result as string,
        });
      };
      reader.readAsDataURL(file);
    }
  }

  createValueStreamDropdown() {
    this.valueStreamFields = [];
    for (let branch of this.data.product.branches) {
      let items = getValueStreamsByBranch(branch).map((d) => d.name);

      this.valueStreamFields.push({
        label: "Value stream " + (this.data.product.branches.length > 1 ? " (" + branch + ")" : ""),
        items: items,
        formControlName: "value-stream-" + branch,
        branch: branch,
      });

      // Handle both formats: object format {branch, name} and string array format
      let selectedValueStream = null;
      if (this.data?.product?.valueStreams) {
        if (Array.isArray(this.data.product.valueStreams) && this.data.product.valueStreams.length > 0) {
          if (typeof this.data.product.valueStreams[0] === "string") {
            const branchIndex = this.data.product.branches.indexOf(branch);
            selectedValueStream = this.data.product.valueStreams[branchIndex] ?? null;
          } else {
            selectedValueStream = this.data.product.valueStreams.find((valueStream) => valueStream.branch == branch)?.name;
          }
        }
      }

      this.addProductForm.addControl("value-stream-" + branch, new FormControl(selectedValueStream));
      this.setFieldRequired("value-stream-" + branch, !this.isDataProductMode && this.addProductForm.get('productType')?.value != 'Legacy' && this.addProductForm.get('productType')?.value != 'Inno');
    }
  }

  addOrRemoveValueStreamDropdownBasedOnBranches(selectedBranches: Array<string>) {
    if (selectedBranches.length > this.oldSelectedBranches.length) {
      // Branch added
      let branchAdded = selectedBranches.find((branch) => !this.oldSelectedBranches.includes(branch));

      let items = getValueStreamsByBranch(branchAdded).map((d) => d.name);

      this.valueStreamFields.push({
        label: "Value stream " + (selectedBranches.length > 1 ? " (" + branchAdded + ")" : ""),
        type: "ng-select",
        items: items,
        formControlName: "value-stream-" + branchAdded,
        multiple: false,
        required: true,
        class: "select-control",
      });

      this.addProductForm.addControl("value-stream-" + branchAdded, new FormControl(null));
      this.setFieldRequired("value-stream-" + branchAdded, !this.isDataProductMode && this.addProductForm.get('productType')?.value != 'Legacy' && this.addProductForm.get('productType')?.value != 'Inno');

      if (selectedBranches.length == 2) {
        // Special case if we are going from 1 to 2, we need to change the label of the first dropdown
        this.valueStreamFields.find((field) => field.formControlName == "value-stream-" + selectedBranches[0]).label =
          "Value stream (" + selectedBranches[0] + ")";
      }
    } else {
      // Branch removed
      let branchRemoved = this.oldSelectedBranches.find((branch) => !selectedBranches.includes(branch));
      this.valueStreamFields.splice(
        this.valueStreamFields.findIndex((field) => field.formControlName == "value-stream-" + branchRemoved),
        1
      );
      this.addProductForm.removeControl("value-stream-" + branchRemoved);

      if (selectedBranches.length == 1) {
        // Special case if we are going from 2 to 1, we need to change the label of the first dropdown
        this.valueStreamFields.find((field) => field.formControlName == "value-stream-" + selectedBranches[0]).label = "Value stream";
      }
    }

    this.oldSelectedBranches = [...selectedBranches];
  }

  isTabProductDetailsInvalid(): boolean {
    const phase = this.addProductForm.get("phase")?.value;
    let requiredFields: string[] = [];
    if(this.isDataProductMode){
      requiredFields = [...this.FIELD_REQUIREMENTS.ALWAYS_REQUIRED, ...this.FIELD_REQUIREMENTS.DATA_PRODUCT.REQUIRED_FIELDS];
    }else if  (this.isLocalProduct) {
      requiredFields = [...this.FIELD_REQUIREMENTS.ALWAYS_REQUIRED];
      requiredFields = requiredFields.concat(this.FIELD_REQUIREMENTS.LOCAL_PRODUCT.PHASE_REQUIREMENTS[phase] ?? []);
    } else {
      requiredFields = [...this.FIELD_REQUIREMENTS.ALWAYS_REQUIRED];
      requiredFields = requiredFields.concat(this.FIELD_REQUIREMENTS.REGULAR_PRODUCT.PHASE_REQUIREMENTS[phase] ?? []);
    }
    // Only check required fields that are in this tab
    const tabFields = this.isLocalProduct ? [...this.TAB_FIELDS.productDetails, "phase"] : this.TAB_FIELDS.productDetails;
    return requiredFields.some((field) => tabFields.includes(field) && this.addProductForm.get(field)?.invalid);
  }


  isTabOrganizationInvalid(): boolean {
    const phase = this.addProductForm.get("phase")?.value;
    let requiredFields: string[] = [];
    if(this.isDataProductMode){
      requiredFields = [...this.FIELD_REQUIREMENTS.ALWAYS_REQUIRED, ...this.FIELD_REQUIREMENTS.DATA_PRODUCT.REQUIRED_FIELDS];
    }else if (this.isLocalProduct) {
      requiredFields = [...this.FIELD_REQUIREMENTS.ALWAYS_REQUIRED];
      requiredFields = requiredFields.concat(this.FIELD_REQUIREMENTS.LOCAL_PRODUCT.PHASE_REQUIREMENTS[phase] ?? []);
    } else {
      requiredFields = [...this.FIELD_REQUIREMENTS.ALWAYS_REQUIRED];
      requiredFields = requiredFields.concat(this.FIELD_REQUIREMENTS.REGULAR_PRODUCT.PHASE_REQUIREMENTS[phase] ?? []);
    }
    // Only check required fields that are in this tab
    const tabFields = this.TAB_FIELDS.organization;
    // Also check all value stream controls
    const valueStreamInvalid = this.valueStreamFields.some((field: any) => this.addProductForm.get(field.formControlName)?.invalid);
    return requiredFields.some((field) => tabFields.includes(field) && this.addProductForm.get(field)?.invalid) || valueStreamInvalid;
  }

  isTabContactsInvalid(): boolean {
    const phase = this.addProductForm.get("phase")?.value;
    let requiredFields: string[] = [];
    if(this.isDataProductMode){
      requiredFields = this.FIELD_REQUIREMENTS.DATA_PRODUCT.REQUIRED_FIELDS;
    } else if (this.isLocalProduct) {
      requiredFields = this.FIELD_REQUIREMENTS.LOCAL_PRODUCT.PHASE_REQUIREMENTS[phase] ?? [];
    } else {
      requiredFields = this.FIELD_REQUIREMENTS.REGULAR_PRODUCT.PHASE_REQUIREMENTS[phase] ?? [];
    }
    const tabFields = this.TAB_FIELDS.contacts;
    return requiredFields.some((field) => {
      if (!tabFields.includes(field)) return false;
      if (field === "pilot" && !this.isPilotRequired()) {
        return false;
      }
      return this.addProductForm.get(field)?.invalid;
    });
  }

  isTabMilestonesInvalid(): boolean {
    const controls = this.isLocalProduct ? this.requiredMilestoneControls : ["phase", ...this.requiredMilestoneControls];
    return controls.some((name) => this.addProductForm.get(name)?.invalid);
  }

  isTabCostsInvalid(): boolean {
    return false;
  }

  isTabDeploymentScopeInvalid(): boolean {
    return false;
  }

  getDialogTitle(): string {
    if (this.isLocalProduct) {
      return this.data?.product?.id ? "Edit Local Product" : "Create Local Product";
    }
    if (this.isEnablerProductMode) {
      return this.data?.product?.id ? "Edit Enabler Product" : "Create Enabler Product";
    }
    if (this.isDataProductMode) {
      return this.data?.product?.id ? "Edit Data Product" : "Add Data Product";
    }
    return this.data?.product?.id ? "Edit Product" : "Add New Product";
  }

  getNameFieldLabel(): string {
    if (this.isDataProductMode) {
      return "Data Product Name";
    } else if (this.isEnablerProductMode) {
      return "Enabler Product Name";
    } else {
      return "Product Name";
    }
  }

  getSubmitButtonLabel(): string {
    if (this.data?.product?.id) {
      return "Save & Close";
    }
    if (this.isDataProductMode) {
      return "Add Data Product";
    } else if (this.isEnablerProductMode) {
      return "Add Enabler Product";
    } else {
      return "Add Product";
    }
  }

  isLocalProductField(fieldName: string): boolean {
    if (!this.isLocalProduct) return true;

    return this.FIELD_REQUIREMENTS.LOCAL_PRODUCT.VISIBLE_FIELDS.includes(fieldName);
  }

  isFieldRequired(fieldName: string): boolean {
    return this.specialFieldIsRequired[fieldName] === true;
  }

  // Document upload methods for local products
  triggerCommunicationKitUpload(): void {
    this.communicationKitInput.nativeElement.click();
  }

  triggerDemoUpload(): void {
    this.demoInput.nativeElement.click();
  }

  onCommunicationKitChange(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (const file of files) {
        this.uploadedCommunicationKits.push(file);
      }
      // Clear the input to allow re-uploading the same file
      event.target.value = "";
    }
  }

  onDemoChange(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (const file of files) {
        this.uploadedDemos.push(file);
      }
      // Clear the input to allow re-uploading the same file
      event.target.value = "";
    }
  }

  removeCommunicationKit(file: File): void {
    const index = this.uploadedCommunicationKits.indexOf(file);
    if (index > -1) {
      this.uploadedCommunicationKits.splice(index, 1);
    }
  }

  removeDemo(file: File): void {
    const index = this.uploadedDemos.indexOf(file);
    if (index > -1) {
      this.uploadedDemos.splice(index, 1);
    }
  }

  // Load existing documents when editing a local product
  loadExistingDocuments(productId: number): void {
    this.toolboxService.loadToolbox({productId}).subscribe({
      next: (toolboxItems) => {
        const documents = toolboxItems.filter((item) => item.type === "document");

        // Separate documents by type based on title
        this.existingCommunicationKits = documents.filter((doc) => doc.title === "Communication Kit");
        this.existingDemos = documents.filter((doc) => doc.title === "Demo");

        // Store backup for cancel functionality
        this.originalExistingCommunicationKits = [...this.existingCommunicationKits];
        this.originalExistingDemos = [...this.existingDemos];
      },
      error: (error) => {
        console.error("Error loading existing documents:", error);
      },
    });
  } // Remove existing documents (mark for deletion, don't delete immediately)
  removeExistingCommunicationKit(document: any): void {
    // Mark document for deletion
    this.documentsToDelete.push(document);

    // Remove from UI immediately
    const index = this.existingCommunicationKits.indexOf(document);
    if (index > -1) {
      this.existingCommunicationKits.splice(index, 1);
    }
  }

  removeExistingDemo(document: any): void {
    // Mark document for deletion
    this.documentsToDelete.push(document);

    // Remove from UI immediately
    const index = this.existingDemos.indexOf(document);
    if (index > -1) {
      this.existingDemos.splice(index, 1);
    }
  }

  // Handle document operations after product is saved
  private handleDocumentOperations(productId: number, productToSave: any): void {
    const allPromises: Observable<any>[] = [];

    // 1. Delete documents marked for deletion
    for (const docToDelete of this.documentsToDelete) {
      allPromises.push(this.toolboxService.deleteDocument({productId}, docToDelete.title));
    }

    // 2. Upload new Communication Kit files
    for (const file of this.uploadedCommunicationKits) {
      allPromises.push(this.toolboxService.saveDocument({productId}, "Communication Kit", file));
    }

    // 3. Upload new Demo files
    for (const file of this.uploadedDemos) {
      allPromises.push(this.toolboxService.saveDocument({productId}, "Demo", file));
    }

    // If there are operations to perform, wait for all to complete
    if (allPromises.length > 0) {
      forkJoin(allPromises).subscribe({
        next: () => {
          this.toastrService.success("Product and documents " + (this.data?.product?.id ? "updated" : "added") + " successfully.", "Success");
          this.dialogRef.close(productToSave);
          this.sharedEventsService.notifyProductUpdated();
        },
        error: (operationError) => {
          console.error("Error with document operations:", operationError);
          this.toastrService.warning("Product saved but some document operations failed. Please check and try again.", "Partial Success");
          this.dialogRef.close(productToSave);
          this.sharedEventsService.notifyProductUpdated();
        },
      });
    } else {
      // No document operations needed, just show success
      this.showSuccessAndClose(productToSave);
    }
  }

  // Show success message and close dialog
  private showSuccessAndClose(productToSave: any): void {
    this.toastrService.success("Product " + (this.data?.product?.id ? "updated" : "added") + " successfully.", "Success");
    this.dialogRef.close(productToSave);
    this.sharedEventsService.notifyProductUpdated();
  }

  deleteProduct(productId: number): void {
    this.dialog
      .open(ConfirmPopupComponent, {
        width: "30vw",
        data: {
          icon: "delete",
          title: "Delete a product",
          text: "Are you sure you want to delete this product ?",
        },
        position: {
          top: "20px",
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res === "confirm") {
          this.portfolioStatusService.deleteProduct(productId).subscribe(
            () => {
              this.toastrService.success("Product has been deleted !");
              this.dialogRef.close(productId);
              this.sharedEventsService.notifyPortfolioStatusUpdated();
              this.sharedEventsService.notifyProductUpdated();
            },
            () => {
              this.toastrService.error("An error occurred while deleting the product.");
            }
          );
        }
      });
  }

  ensureTwoNumberDecimals(formName: string) {
    setTimeout(() => {
      if (String(this.addProductForm.get(formName)?.value).split(".")[1]?.length > 2) {
        const value = Number.parseFloat(this.addProductForm.get(formName)?.value);
        this.addProductForm.get(formName).setValue(Number.isNaN(value) ? null : Math.trunc(value * 100) / 100);
      }
    }, 0);
  }

  getBackgroundColorOfStatus(status: string) {
    return getBackgroundColorOfStatus(status);
  }

  getSortedDepScopeBranches(): string[] {
    return [...this.addProductForm.get('branches')?.value ?? []].sort((a, b) => a.localeCompare(b));
  }

  selectDepScopeBranch(branch: string): void {
    this.selectedDepScopeBranch = branch;

    this.buildNodesToDisplay();
  }

  buildScopeTreesFromAssets(selectedBranches: string[]): void {
    for (const branch of selectedBranches) {
      this.scopeByBranch[branch] = ScopeTreeUtil.buildScopeTree(this.assets.filter(a => a.branch === branch));
    }
  }

  selectScopeBasedOnExisting() {
    for (const branch in this.scopeByBranch) {
      ScopeTreeUtil.selectScopesInTree(this.scopeByBranch[branch], this.data.product.scopes);
    }
  }

  buildNodesToDisplay(): void {
    const rootNodes = this.scopeByBranch[this.selectedDepScopeBranch] ?? [];
    const query = this.depScopeSearchQuery.toLowerCase().trim();
    this.nodesToDisplay = [];

    this.buildNodesToDisplayAux(rootNodes, 0, query);
    this.selectedChips = ScopeTreeUtil.computeSelectedChips(rootNodes);
  }

  buildNodesToDisplayAux(nodes: ScopeNode[], depth: number, query: string) {
    for (const node of nodes) {
      const leafStats = this.computeLeafStats(node);
      if (query != null && query != "") {
        if (this.nodeOrDescendantsMatch(node, query)) {
          this.nodesToDisplay.push({node, depth, leafStats});
          if (node.children?.length) {
            this.buildNodesToDisplayAux(node.children, depth + 1, query);
          }
        }
      } else {
        this.nodesToDisplay.push({node, depth, leafStats});
        if (!node.collapsed && node.children?.length) {
          this.buildNodesToDisplayAux(node.children, depth + 1, query);
        }
      }
    }
  }

  computeLeafStats(node: ScopeNode): { selected: number; total: number } {
    if (!node.children?.length) {
      return {selected: node.selected === 'yes' ? 1 : 0, total: 1};
    }
    return node.children.reduce((acc, child) => {
      const stat = this.computeLeafStats(child);
      return {selected: acc.selected + stat.selected, total: acc.total + stat.total};
    }, {selected: 0, total: 0});
  }

  shouldShowAddNewSiteRow(index: number): boolean {
    const currentItem = this.nodesToDisplay[index];
    const nextItem = this.nodesToDisplay[index + 1];
    
    // Case 1: Asset with no sites - show "Add New" right after the asset
    if (currentItem.depth === 2 && !currentItem.node.collapsed && (!currentItem.node.children || currentItem.node.children.length === 0)) {
      return true;
    }
    
    // Case 2: After the last site of an asset - show "Add New" after the last site
    // Detection: current item is a site (depth 3) AND next item is not a site (depth <= 2 or undefined)
    if (currentItem.depth === 3 && (!nextItem || nextItem.depth <= 2)) {
      return true;
    }
    
    return false;
  }

  getAssetNodeForAddNewRow(index: number): ScopeNode | null {
    const currentItem = this.nodesToDisplay[index];
    
    // If current item is an asset (depth 2), it's the asset itself (case of asset with no sites)
    if (currentItem.depth === 2) {
      return currentItem.node;
    }
    
    // If current item is a site (depth 3), find the parent Asset node by searching backwards
    for (let i = index - 1; i >= 0; i--) {
      const item = this.nodesToDisplay[i];
      if (item.depth === 2) {
        return item.node;
      }
    }
    return null;
  }

  nodeOrDescendantsMatch(node: ScopeNode, query: string): boolean {
    if (node.name.toLowerCase().includes(query)) return true;
    return node.children?.some(child => this.nodeOrDescendantsMatch(child, query)) ?? false;
  }

  onDepScopeSearchChange(query: string): void {
    this.depScopeSearchQuery = query;
    this.buildNodesToDisplay();
  }

  toggleScopeCollapse(node: ScopeNode): void {
    node.collapsed = !node.collapsed;
    this.buildNodesToDisplay();
  }

  toggleScopeSelect(node: ScopeNode): void {
    ScopeTreeUtil.setNodeAndDescendantsSelected(node, node.selected == 'yes' ? 'no' : 'yes');
    ScopeTreeUtil.refreshParentStates(this.scopeByBranch[this.selectedDepScopeBranch] ?? []);
    this.buildNodesToDisplay();
  }

  removeChip(chipName: string): void {
    const rootNodes = this.scopeByBranch[this.selectedDepScopeBranch] ?? [];
    this.removeChipAux(rootNodes, chipName);
    ScopeTreeUtil.refreshParentStates(rootNodes);
    this.buildNodesToDisplay();
  }

  removeChipAux(nodes: ScopeNode[], chipName: string): boolean {
    for (const node of nodes) {
      if (node.name === chipName) {
        ScopeTreeUtil.setNodeAndDescendantsSelected(node, 'no');
        return true;
      }
      if (node.children?.length && this.removeChipAux(node.children, chipName)) return true;
    }
    return false;
  }

  buildScopeToSendOnSave(): any[] {
    let scopeToSave = [];

    for (let branch in this.scopeByBranch) {
      for (let node of this.scopeByBranch[branch]) {
        scopeToSave = this.buildScopeToSendOnSaveAux(node, 0, scopeToSave);
      }
    }

    return scopeToSave;
  }

  buildScopeToSendOnSaveAux(node: ScopeNode, currentDepth: number, currentScopeList: any[]): any[] {
    if (node.selected == "yes" && node.id >= 0) {
      let propertyToPush = Object.entries(ScopeTreeUtil.SCOPE_DEPTHS_BY_OBJECT).find(([key, depth]) => depth == currentDepth)[0];
      let object = {};
      object[propertyToPush] = node.id;
      if (!currentScopeList.some(s => JSON.stringify(s) == JSON.stringify(object))) currentScopeList.push(object);
    }
    else if (node.selected == "partial" && node.children?.length > 0) {
      for (let children of node.children) {
        currentScopeList = this.buildScopeToSendOnSaveAux(children, currentDepth + 1, currentScopeList);
      }
    }
    return currentScopeList;
  }

  addNewSite(assetNode: ScopeNode): void {
    const siteName = this.newSiteNames[assetNode.id]?.trim();
    
    if (!siteName) {
      return;
    }

    // Check if site already exists for this asset
    const siteExists = assetNode.children?.some(site => 
      site.name.toLowerCase() === siteName.toLowerCase()
    );

    if (siteExists) {
      this.toastrService.warning(`A site named "${siteName}" already exists for this asset`);
      return;
    }
    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      data: {
        title: "Add New Site",
        text: `
          <span class="warning-section">
            <span class="material-icons-outlined" aria-hidden="true">warning_amber</span>
            <span><strong>Warning:</strong> Are you sure you want to add "${siteName}"? It will be created for the whole application and users.</span>
          </span>
          <span style="display: block; margin: 16px 0 0 4px;"><strong>Info:</strong> The site will only be created when you save the product with this site selected.</span>
         `,
        icon: 'info',
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed === 'confirm') {
        // Add site locally with temporary ID
        const newSiteNode: ScopeNode = {
          id: this.temporarySiteIdCounter--,
          name: siteName,
          selected: 'yes',
          collapsed: false,
          children: [],
          isNew: true,
          assetId: assetNode.id
        };

        if (!assetNode.children) {
          assetNode.children = [];
        }
        assetNode.children.push(newSiteNode);

        // Clear input
        this.newSiteNames[assetNode.id] = '';

        // Update parent states and refresh display
        ScopeTreeUtil.refreshParentStates(this.scopeByBranch[this.selectedDepScopeBranch] ?? []);
        this.buildNodesToDisplay();
      }
    });
  }

  private buildNewSitesToSave(): { name: string; assetId: number }[] {
    const newSites: { name: string; assetId: number }[] = [];
    for (const branch in this.scopeByBranch) {
      this.collectNewSelectedSites(this.scopeByBranch[branch], newSites);
    }

    return newSites;
  }

  private collectNewSelectedSites(nodes: ScopeNode[], collector: { name: string; assetId: number }[]): void {
    for (const node of nodes) {
      if (node.isNew && node.selected === 'yes' && node.assetId) {
        collector.push({ name: node.name, assetId: node.assetId });
      }
      if (node.children?.length) {
        this.collectNewSelectedSites(node.children, collector);
      }
    }
  }

  private removeUnselectedNewSites(): void {
    for (const branch in this.scopeByBranch) {
      this.removeUnselectedNewSitesFromNodes(this.scopeByBranch[branch]);
    }
  }

  private removeUnselectedNewSitesFromNodes(nodes: ScopeNode[]): void {
    for (const node of nodes) {
      if (node.children?.length) {
        node.children = node.children.filter(child => !(child.isNew && child.selected !== 'yes'));
        this.removeUnselectedNewSitesFromNodes(node.children);
      }
    }
  }
}

